import { useState } from 'react'

import { ChatFilled } from 'src/shared/components/icons/chat/chat-filled'
import { PhoneFilled } from 'src/shared/components/icons/phone-filled'
import { VerificationBadge } from 'src/shared/components/icons/verification-badge'
import { BottomSheet } from 'src/shared/components/ui/bottom-sheet'
import { Button } from 'src/shared/components/ui/buttons/button'
import TruncatedText from 'src/shared/components/ui/miscellaneous/truncated-text'
import useBreakpoint from 'src/shared/hooks/use-breakpoint'
import { useWindowSize } from 'src/shared/hooks/use-window-size'
import { cn } from 'src/shared/utils/cn'

import { useUserReviews } from '../../api/marketplace/use-user-reviews'
import { professionMap } from '../../schemas/profession'
import type { PublicUserData } from '../../schemas/user'
import CoverPhoto, { CoverPhotoFallback } from '../profile-photos/cover-photo'
import ProfileAvatar from '../profile-photos/profile-avatar'
import BioModal from '../public-profile/bio/bio-modal'
import OtherInfoContainer from '../public-profile/other-info-container'
import RequestCallButton from '../public-profile/ui/request-call-btn'

interface UserProfileCardProps {
  user: PublicUserData
  isPublic?: boolean
  primaryButtonText?: string
  primaryButtonOnClick?: () => void

  /** Only work when isPublic is false */
  phoneButtonOnClick?: () => void
  /** Only work when isPublic is false */
  chatButtonOnClick?: () => void

  showPrimaryButton?: boolean
  showPhoneButton?: boolean
}

export default function UserProfileCard({
  user,
  isPublic = false,
  primaryButtonText,
  primaryButtonOnClick,
  phoneButtonOnClick,
  chatButtonOnClick,
  showPrimaryButton = true,
  showPhoneButton = true,
}: UserProfileCardProps) {
  const [bioModalOpen, setBioModalOpen] = useState(false)

  const { isSmTabletOrSmaller } = useBreakpoint()
  const { width } = useWindowSize()

  const { data: reviewData } = useUserReviews(user?.id || -1)
  const reviewsCount = reviewData?.totalReviews ?? 0
  const averageRating = reviewData?.averageRating ?? 0

  const isVerified = user.verified || false
  const showChatButton = !isPublic
  const professionText = professionMap[user.profession].label

  if (isSmTabletOrSmaller) {
    return (
      <>
        <div className="flex w-full flex-col items-start gap-4 bg-white">
          <img
            src={user.coverPhoto || CoverPhotoFallback}
            alt="cover photo"
            className="h-[160px] w-full object-cover"
          />
          <div className="mb-[-50px] flex w-full translate-y-[-50px] flex-col rounded-[20px] bg-white sm:rounded-[40px]">
            <div className="mb-[-50px] flex translate-y-[-50px] flex-col gap-2">
              <div className="flex flex-col items-center px-5 lg:px-8">
                <ProfileAvatar
                  userId={user.id}
                  size={108}
                  className="border-4 border-white shadow-[0_1px_12px_1px_rgba(27,48,41,0.10)]"
                  shouldZoom={true}
                />
                <div className="my-[12px] flex flex-col items-center">
                  <h1 className={'mb-1 text-center text-[24px] font-semibold leading-[100%]'}>
                    <span className="mr-1">{user.fullName}</span>
                    <VerificationBadge isVerified={user.verified || false} className="inline-block" size={20} />
                  </h1>
                  <div className="flex items-center gap-2" id="mobile-public-profile-profession">
                    <p className="text-[15px] text-muted">{professionMap[user.profession].label}</p>
                  </div>
                </div>
                {!user.biography ? (
                  <div className="relative w-full items-center text-center text-[13px] italic text-muted">
                    <p className="line-clamp-1">{user.fullName} doesn&apos;t have a bio yet.</p>
                  </div>
                ) : (
                  <TruncatedText
                    text={user.biography || ''}
                    numberOfLines={2}
                    customOverflowComponent={
                      <button
                        className="inline pl-1 text-primary hover:underline"
                        onClick={() => setBioModalOpen(true)}
                      >
                        Read full bio
                      </button>
                    }
                    className="relative w-full items-center text-center text-[13px] leading-[150%] text-muted"
                  />
                )}
                <OtherInfoContainer
                  isCurrentUser={false}
                  reviewsCount={reviewsCount}
                  averageRating={averageRating}
                  shortAddress={user.shortAddress || ''}
                  className="mt-[12px] block text-center text-[12px]"
                  starSize={12}
                />
                {/* <div className="my-4 h-px w-full bg-[#efefef] lg:my-7" /> */}
              </div>
            </div>
          </div>
        </div>
        <BottomSheet open={bioModalOpen} close={() => setBioModalOpen(false)}>
          <div className="flex flex-col gap-4">
            <h1 className="text-[26px] font-bold">About {user.firstName}</h1>
            <p className="text-[15px] text-muted md:text-base">{user.biography}</p>
          </div>
        </BottomSheet>
      </>
    )
  }

  return (
    <div className={cn('relative')}>
      <CoverPhoto
        userId={user.id}
        className={cn('h-[284px] w-full rounded-b-[30px] border-b', isPublic && 'rounded-[30px]')}
      />
      <div className="mb-[-150px] flex translate-y-[-150px] justify-center px-[20px] lg:mb-[-174px] lg:translate-y-[-174px]">
        <div className="flex h-[180px] w-full max-w-[1000px] flex-row items-center justify-between gap-6 rounded-[30px] bg-white pl-[20px] pr-[40px] text-primary shadow-[0_2px_20px_0_rgba(27,48,41,0.12)] lg:h-[225px] lg:gap-10 lg:pl-[40px] lg:pr-[60px] xl:gap-[78px]">
          <div className="flex flex-row items-center gap-2">
            <ProfileAvatar
              userId={user.id}
              size={width < 1024 ? 120 : 150}
              className="self-center border-[5px] border-white shadow-[0_1px_12px_1px_rgba(27,48,41,0.10)]"
              shouldZoom={true}
            />

            <div className="ml-[15px] flex min-w-[250px] flex-col lg:ml-[30px]">
              <h2 className={'mb-[6px] line-clamp-3 text-[24px] font-bold leading-[100%] lg:text-[30px]'}>
                <span className="mr-[5px]">{user.fullName}</span>
                <VerificationBadge isVerified={isVerified} className="mb-0.5 inline-block" size={24} />
              </h2>
              <p className="text-[14px] text-muted lg:text-[16px]">{professionText}</p>

              {(showChatButton || showPhoneButton || showPrimaryButton) && (
                <div className="mt-[18px] flex flex-row gap-[10px]">
                  {showPrimaryButton && (
                    <Button
                      variant="default"
                      className="h-[45px] min-w-[160px] rounded-full bg-primary text-[14px] font-semibold text-white lg:min-w-[200px]"
                      onClick={primaryButtonOnClick}
                    >
                      {primaryButtonText}
                    </Button>
                  )}
                  {showPhoneButton &&
                    (isPublic ? (
                      <RequestCallButton username={user.username} type="icon" />
                    ) : (
                      <Button
                        variant="default"
                        className="size-[50px] shrink-0 rounded-full bg-[#F8F7F5] p-0"
                        onClick={phoneButtonOnClick}
                      >
                        <PhoneFilled size={22} className="text-primary" />
                      </Button>
                    ))}
                  {showChatButton && (
                    <Button
                      variant="default"
                      className="size-[50px] shrink-0 rounded-full bg-[#F8F7F5] p-0"
                      onClick={chatButtonOnClick}
                    >
                      <ChatFilled size={22} className="text-primary" />
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex w-full min-w-0 flex-col gap-3">
            <OtherInfoContainer
              isCurrentUser={false}
              reviewsCount={reviewsCount}
              averageRating={averageRating}
              shortAddress={user.shortAddress || ''}
              className="block lg:text-[16px]"
            />
            {!user.biography ? (
              <div className="relative w-full text-left text-sm italic text-muted lg:text-[17px]">
                <p className="line-clamp-1">{user.fullName} doesn&apos;t have a bio yet.</p>
              </div>
            ) : (
              <TruncatedText
                key={'desktop-bio'}
                text={user.biography || ''}
                numberOfLines={3}
                customOverflowComponent={
                  <button className="inline bg-white pl-1 text-primary underline" onClick={() => setBioModalOpen(true)}>
                    More
                  </button>
                }
                className="relative w-full min-w-[120px] text-left leading-[150%] md:text-[14px] lg:text-[15px]"
              />
            )}
          </div>
        </div>
      </div>
      <BioModal isOpen={bioModalOpen} setOpen={setBioModalOpen} user={user} />
    </div>
  )
}
